import React, { useCallback, useEffect, useState } from 'react'
import { Bot, CheckCircle2, History, LoaderCircle, Play, Square, TriangleAlert } from 'lucide-react'
import { api, getApiErrorMessage } from '../../lib/api'

function unwrap(response) {
  return response?.data?.data ?? response?.data ?? null
}

function number(value, suffix = '') {
  return `${new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 1 }).format(Number(value) || 0)}${suffix}`
}

function dateTime(value) {
  if (!value) return ''
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString('ko-KR')
}

const TERMINAL = new Set(['SUCCEEDED', 'FAILED', 'CANCELLED', 'TIMED_OUT'])

export default function CounselPracticeAsyncRegressionRunner() {
  const [samples, setSamples] = useState(3)
  const [running, setRunning] = useState(false)
  const [activeRun, setActiveRun] = useState(null)
  const [history, setHistory] = useState([])
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')

  const loadHistory = useCallback(async () => {
    try {
      const response = await api.get('/counsel-practice/admin/quality/regressions', {
        params: { limit: 10 },
        __silent: true,
      })
      const data = unwrap(response)
      setHistory(Array.isArray(data?.items) ? data.items : [])
    } catch (e) {
      setHistory([])
    }
  }, [])

  useEffect(() => { loadHistory() }, [loadHistory])

  useEffect(() => {
    if (!running || !activeRun?.runKey) return undefined
    let disposed = false
    let requesting = false
    const poll = async () => {
      if (requesting) return
      requesting = true
      try {
        const response = await api.get(`/counsel-practice/admin/quality/regressions/${activeRun.runKey}`, { __silent: true })
        const next = unwrap(response)
        if (disposed || !next) return
        setActiveRun(next)
        if (next.result) setResult(next.result)
        if (TERMINAL.has(next.status)) {
          setRunning(false)
          if (next.status !== 'SUCCEEDED') setError(next.errorMessage || `${next.statusLabel} 상태로 점검이 끝났습니다.`)
          await loadHistory()
        }
      } catch (e) {
        if (!disposed) {
          setRunning(false)
          setError(getApiErrorMessage(e, '점검 진행 상태를 확인하지 못했습니다.'))
        }
      } finally {
        requesting = false
      }
    }
    poll()
    const timer = window.setInterval(poll, 900)
    return () => {
      disposed = true
      window.clearInterval(timer)
    }
  }, [running, activeRun?.runKey, loadHistory])

  const run = async () => {
    if (running) return
    setRunning(true)
    setResult(null)
    setError('')
    try {
      const response = await api.post('/counsel-practice/admin/quality/regression', null, {
        params: { samples },
        __silent: true,
      })
      const next = unwrap(response)
      setActiveRun(next)
      if (!next?.runKey) throw new Error('점검 번호를 받지 못했습니다.')
    } catch (e) {
      setRunning(false)
      setError(getApiErrorMessage(e, '실제 AI 점검을 시작하지 못했습니다. 잠시 후 다시 시도해 주세요.'))
    }
  }

  const cancel = async () => {
    if (!activeRun?.runKey || !activeRun?.cancellable) return
    try {
      const response = await api.delete(`/counsel-practice/admin/quality/regressions/${activeRun.runKey}`, { __silent: true })
      setActiveRun(unwrap(response))
    } catch (e) {
      setError(getApiErrorMessage(e, '점검을 취소하지 못했습니다.'))
    }
  }

  const openRun = (item) => {
    setActiveRun(item)
    setResult(item.result || null)
    setError(item.errorMessage || '')
    setRunning(!TERMINAL.has(item.status))
  }

  const items = Array.isArray(result?.items) ? result.items : []

  return (
    <section className="mt-6 border-t border-slate-200 pt-5" aria-labelledby="ai-regression-title" data-testid="counsel-ai-regression">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Bot className="h-5 w-5 text-sky-700" aria-hidden="true" />
          <h3 id="ai-regression-title" className="text-base font-semibold text-slate-950">실제 AI 대화 점검</h3>
        </div>
        <div className="flex items-center gap-2">
          <select value={samples} onChange={(event) => setSamples(Number(event.target.value))} disabled={running} className="h-9 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-700" aria-label="점검할 대표 대화 수">
            <option value={3}>대표 대화 3건</option>
            <option value={5}>대표 대화 5건</option>
          </select>
          {activeRun?.cancellable && <button type="button" onClick={cancel} className="inline-flex h-9 items-center gap-2 rounded-md border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"><Square className="h-3.5 w-3.5" aria-hidden="true" />중지</button>}
          <button type="button" onClick={run} disabled={running} className="inline-flex h-9 items-center gap-2 rounded-md bg-sky-700 px-3 text-sm font-semibold text-white hover:bg-sky-800 disabled:cursor-not-allowed disabled:opacity-50" data-testid="counsel-run-ai-regression">
            {running ? <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Play className="h-4 w-4" aria-hidden="true" />}
            {running ? '점검 중' : '점검 실행'}
          </button>
        </div>
      </div>

      {activeRun && <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 border-l-2 border-sky-500 bg-sky-50 px-3 py-2 text-sm text-slate-700" role="status"><span className="font-semibold text-sky-800">{activeRun.statusLabel}</span><span>대표 대화 {activeRun.sampleCount}건</span><span>{dateTime(activeRun.createdAt)}</span></div>}
      {error && <div className="mt-3 border-l-2 border-rose-500 bg-rose-50 px-3 py-2 text-sm text-rose-700" role="alert">{error}</div>}

      {result && (
        <>
          <dl className="mt-4 grid border-y border-slate-200 sm:grid-cols-3 lg:grid-cols-6">
            {[
              ['결과', result.statusLabel],
              ['통과율', number(result.passRate, '%')],
              ['실제 AI 응답', number(result.actualAiCount, '건')],
              ['기본 응답', number(result.fallbackCount, '건')],
              ['평균 응답', number((result.averageLatencyMs || 0) / 1000, '초')],
              ['사용 토큰', number(result.totalTokens)],
            ].map(([label, value]) => <div key={label} className="px-3 py-3"><dt className="text-xs text-slate-500">{label}</dt><dd className="mt-1 text-sm font-semibold text-slate-900">{value}</dd></div>)}
          </dl>

          <div className="mt-4 overflow-x-auto border-y border-slate-200">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="px-4 py-3 font-medium">연습</th><th className="px-4 py-3 font-medium">고객 유형</th><th className="px-4 py-3 font-medium">응답</th><th className="px-4 py-3 font-medium">판정</th><th className="px-4 py-3 font-medium">세부 점검</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((item, index) => (
                  <tr key={`${item.contentTitle}-${item.personaLabel}-${index}`}>
                    <td className="max-w-[18rem] px-4 py-3"><div className="font-medium text-slate-900">{item.contentTitle}</div><div className="mt-1 text-xs text-slate-500">{item.insuranceTypeLabel}</div></td>
                    <td className="px-4 py-3 text-slate-700">{item.personaLabel}</td>
                    <td className="max-w-[22rem] px-4 py-3 text-slate-700"><p className="line-clamp-3 leading-5">{item.customerReply}</p><span className="mt-1 block text-xs text-slate-500">{item.sourceLabel} · {number((item.latencyMs || 0) / 1000, '초')}</span></td>
                    <td className="px-4 py-3"><span className={`inline-flex items-center gap-1 font-semibold ${item.status === 'PASS' ? 'text-emerald-700' : 'text-amber-700'}`}>{item.status === 'PASS' ? <CheckCircle2 className="h-4 w-4" aria-hidden="true" /> : <TriangleAlert className="h-4 w-4" aria-hidden="true" />}{item.statusLabel} · {number(item.score, '점')}</span></td>
                    <td className="px-4 py-3 text-xs text-slate-600">{(item.checks || []).map((check) => `${check.passed ? '통과' : '확인'}: ${check.label}`).join(' · ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {history.length > 0 && <div className="mt-5" data-testid="counsel-ai-regression-history"><div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><History className="h-4 w-4 text-slate-500" aria-hidden="true" />최근 점검</div><div className="mt-2 divide-y divide-slate-200 border-y border-slate-200">{history.map((item) => <button key={item.runKey} type="button" onClick={() => openRun(item)} className="flex w-full flex-wrap items-center justify-between gap-2 px-2 py-3 text-left text-sm hover:bg-slate-50"><span className="font-medium text-slate-800">{item.statusLabel} · 대표 대화 {item.sampleCount}건</span><span className="text-xs text-slate-500">{dateTime(item.createdAt)}</span></button>)}</div></div>}
    </section>
  )
}
